import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { PointerLockControls } from "@react-three/drei";
import * as THREE from "three";

// ---------- Room geometry ----------
const ROOM = {
  width: 18,
  depth: 18,
  height: 5,
};

// Use a procedural canvas as fallback wall/floor texture (no asset deps)
function useProcTexture({ color = "#1a1410", lines = false, light = false }) {
  return useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 512;
    c.height = 512;
    const ctx = c.getContext("2d");
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 512, 512);
    if (lines) {
      // Parquet planks - more visible
      ctx.strokeStyle = "rgba(0,0,0,0.55)";
      ctx.lineWidth = 2;
      for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.moveTo(0, i * 64);
        ctx.lineTo(512, i * 64);
        ctx.stroke();
      }
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 16; i++) {
        ctx.beginPath();
        ctx.moveTo(i * 32, 0);
        ctx.lineTo(i * 32, 512);
        ctx.stroke();
      }
    }
    if (light) {
      // Subtle wall paper noise
      const imgData = ctx.getImageData(0, 0, 512, 512);
      for (let i = 0; i < imgData.data.length; i += 4) {
        const n = (Math.random() - 0.5) * 12;
        imgData.data[i] = Math.max(0, Math.min(255, imgData.data[i] + n));
        imgData.data[i + 1] = Math.max(0, Math.min(255, imgData.data[i + 1] + n));
        imgData.data[i + 2] = Math.max(0, Math.min(255, imgData.data[i + 2] + n));
      }
      ctx.putImageData(imgData, 0, 0);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(lines ? 6 : 2, lines ? 6 : 2);
    return tex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, lines, light]);
}

// ---------- Single painting ----------
function Painting({ artwork, wall, position, onOpen }) {
  // Prefer the small thumb_data (max 1024px, ~150KB) when available — huge
  // texture uploads (2400px, ~2MB each) were the biggest cause of the 3s
  // homepage delay. Falls back to full image_data for legacy artworks.
  const src = artwork.thumb_data || artwork.image_data;
  const tex = useLoader(THREE.TextureLoader, src);
  const [hovered, setHovered] = useState(false);

  // Compute frame aspect from texture
  const aspect = tex.image?.width && tex.image?.height
    ? tex.image.width / tex.image.height
    : 1;

  // Painting target physical size: longest side ~3.4 units
  const long = 3.4;
  const w = aspect >= 1 ? long : long * aspect;
  const h = aspect >= 1 ? long / aspect : long;

  // Frame padding
  const pad = 0.12;
  const fw = w + pad * 2;
  const fh = h + pad * 2;

  // Wall orientation -> rotation
  const rotation = {
    back: [0, 0, 0],
    front: [0, Math.PI, 0],
    left: [0, Math.PI / 2, 0],
    right: [0, -Math.PI / 2, 0],
  }[wall];

  // Spotlight from the ceiling
  const lightTarget = useRef();
  const spotRef = useRef();
  useEffect(() => {
    if (spotRef.current && lightTarget.current) {
      spotRef.current.target = lightTarget.current;
    }
  });

  useEffect(() => {
    document.body.style.cursor = hovered ? "pointer" : "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [hovered]);

  return (
    <group position={position} rotation={rotation}>
      {/* Spotlight pointing at the painting */}
      <spotLight
        ref={spotRef}
        position={[0, ROOM.height / 2 - 0.5, 0.6]}
        angle={0.6}
        penumbra={0.5}
        intensity={6}
        distance={6}
        color="#ffd9a3"
      />
      <object3D ref={lightTarget} position={[0, 0, 0]} />

      {/* Frame (slightly behind painting) */}
      <mesh position={[0, 0, 0.005]}>
        <planeGeometry args={[fw, fh]} />
        <meshStandardMaterial color="#2a1a10" roughness={0.85} />
      </mesh>
      {/* Inner dark-wood liner */}
      <mesh position={[0, 0, 0.01]}>
        <planeGeometry args={[w + 0.04, h + 0.04]} />
        <meshStandardMaterial color="#3b2416" roughness={0.75} />
      </mesh>
      {/* The artwork */}
      <mesh
        position={[0, 0, 0.015]}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(artwork);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={() => setHovered(false)}
        scale={hovered ? 1.02 : 1}
      >
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial map={tex} toneMapped={false} />
      </mesh>
      {/* Plaque under painting */}
      <mesh position={[0, -h / 2 - 0.4, 0.01]}>
        <planeGeometry args={[1.4, 0.18]} />
        <meshStandardMaterial color="#161310" />
      </mesh>
    </group>
  );
}

// ---------- Room geometry mesh ----------
function Room() {
  const wallTex = useProcTexture({ color: "#12414c", light: true });
  const floorTex = useProcTexture({ color: "#0a2830", lines: true });
  const ceilingTex = useProcTexture({ color: "#071b21" });

  return (
    <group>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[ROOM.width, ROOM.depth]} />
        <meshStandardMaterial map={floorTex} />
      </mesh>
      {/* Ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, ROOM.height, 0]}>
        <planeGeometry args={[ROOM.width, ROOM.depth]} />
        <meshStandardMaterial map={ceilingTex} />
      </mesh>
      {/* Back wall */}
      <mesh position={[0, ROOM.height / 2, -ROOM.depth / 2]}>
        <planeGeometry args={[ROOM.width, ROOM.height]} />
        <meshStandardMaterial map={wallTex} />
      </mesh>
      {/* Front wall (behind initial camera) */}
      <mesh
        position={[0, ROOM.height / 2, ROOM.depth / 2]}
        rotation={[0, Math.PI, 0]}
      >
        <planeGeometry args={[ROOM.width, ROOM.height]} />
        <meshStandardMaterial map={wallTex} />
      </mesh>
      {/* Left wall */}
      <mesh
        position={[-ROOM.width / 2, ROOM.height / 2, 0]}
        rotation={[0, Math.PI / 2, 0]}
      >
        <planeGeometry args={[ROOM.depth, ROOM.height]} />
        <meshStandardMaterial map={wallTex} />
      </mesh>
      {/* Right wall */}
      <mesh
        position={[ROOM.width / 2, ROOM.height / 2, 0]}
        rotation={[0, -Math.PI / 2, 0]}
      >
        <planeGeometry args={[ROOM.depth, ROOM.height]} />
        <meshStandardMaterial map={wallTex} />
      </mesh>

      {/* Ambient + key fill so it isn't pitch dark */}
      <ambientLight intensity={0.7} color="#fff5e6" />
      <hemisphereLight skyColor="#fff5e6" groundColor="#1a120c" intensity={0.5} />
      {/* A few warm ceiling lights in the room center */}
      <pointLight position={[0, ROOM.height - 0.3, 0]} intensity={1.5} distance={12} color="#ffd9a3" />
      <pointLight position={[-ROOM.width / 3, ROOM.height - 0.3, -ROOM.depth / 3]} intensity={1.0} distance={10} color="#ffd9a3" />
      <pointLight position={[ROOM.width / 3, ROOM.height - 0.3, ROOM.depth / 3]} intensity={1.0} distance={10} color="#ffd9a3" />
    </group>
  );
}

// ---------- Auto-rotating 360 camera in the room center ----------
// - Camera lives at the middle of the room, at eye-height
// - Rotates slowly around Y (auto-spin) whenever the visitor isn't interacting
// - Drag with mouse (desktop, without pointer-lock) or touch (mobile) pans
// - When PointerLock engages ("Enter walk-mode"), this component yields control
function PanoramaCamera({ locked, canvasEl }) {
  const { camera, gl } = useThree();
  const yaw = useRef(0); // rotation around Y (looking left/right)
  const pitch = useRef(0); // rotation around X (looking up/down)
  const dragging = useRef(false);
  const lastTouchMs = useRef(0);
  const start = useRef({ x: 0, y: 0, yaw: 0, pitch: 0 });

  // Park the camera in the middle of the room, at standing height.
  useEffect(() => {
    camera.position.set(0, 1.7, 0);
    camera.rotation.order = "YXZ";
  }, [camera]);

  // Drag-to-look-around: attach listeners to the canvas element so paintings
  // still receive click events (drag is detected by movement threshold).
  useEffect(() => {
    const el = canvasEl?.current || gl.domElement;
    if (!el) return;
    const dragThreshold = 4; // px before we start rotating
    let moved = 0;

    const onDown = (e) => {
      if (locked) return; // pointer-lock owns the camera
      dragging.current = true;
      moved = 0;
      const p = "touches" in e ? e.touches[0] : e;
      start.current = {
        x: p.clientX,
        y: p.clientY,
        yaw: yaw.current,
        pitch: pitch.current,
      };
    };

    const onMove = (e) => {
      if (!dragging.current || locked) return;
      const p = "touches" in e ? e.touches[0] : e;
      const dx = p.clientX - start.current.x;
      const dy = p.clientY - start.current.y;
      moved = Math.max(moved, Math.hypot(dx, dy));
      if (moved > dragThreshold) {
        // 0.005 rad/px feels natural at ~1440px width
        yaw.current = start.current.yaw - dx * 0.005;
        pitch.current = Math.max(
          -Math.PI / 3,
          Math.min(Math.PI / 3, start.current.pitch - dy * 0.004),
        );
        lastTouchMs.current = performance.now();
        // Prevent iOS Safari from treating the drag as a scroll gesture
        if ("touches" in e && e.cancelable) e.preventDefault();
      }
    };

    const onUp = () => {
      dragging.current = false;
    };

    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("touchstart", onDown, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onUp);
    el.addEventListener("touchcancel", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("touchstart", onDown);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onUp);
      el.removeEventListener("touchcancel", onUp);
    };
  }, [gl, canvasEl, locked]);

  useFrame((_, dt) => {
    if (locked) return; // walk-mode: PointerLockControls drives the camera
    // Auto-spin unless the visitor is actively dragging or just finished
    const idle = !dragging.current && performance.now() - lastTouchMs.current > 1200;
    if (idle) {
      yaw.current += dt * 0.08; // ~1 full rotation every ~80s — slow and cinematic
    }
    // Ease pitch back toward horizon when idle
    if (idle) {
      pitch.current += (0 - pitch.current) * Math.min(1, dt * 0.4);
    }
    camera.rotation.y = yaw.current;
    camera.rotation.x = pitch.current;
    camera.rotation.z = 0;
  });

  return null;
}

// ---------- Distribute paintings on walls ----------
function distributePaintings(artworks) {
  // Show the NEWEST uploads first. The API sorts by order ASC (oldest first),
  // so reverse before slicing so the freshest work hangs on the primary wall.
  const sorted = [...artworks].reverse();
  // We have 4 walls, evenly distribute. Each wall slot is at y = ROOM.height/2.
  const w = ROOM.width;
  const d = ROOM.depth;
  const y = 2.0;
  const slotsPerWall = 3;
  // Pre-defined X offsets along the long wall axis
  const offsets = (n) => {
    if (n === 1) return [0];
    if (n === 2) return [-w / 4, w / 4];
    return [-w / 3.2, 0, w / 3.2];
  };

  const result = [];
  const walls = [
    { name: "back", normal: [0, 0, 1], wallZ: -d / 2 + 0.05, axis: "x" },
    { name: "front", normal: [0, 0, -1], wallZ: d / 2 - 0.05, axis: "x" },
    { name: "left", normal: [1, 0, 0], wallX: -w / 2 + 0.05, axis: "z" },
    { name: "right", normal: [-1, 0, 0], wallX: w / 2 - 0.05, axis: "z" },
  ];

  let i = 0;
  for (const wall of walls) {
    const count = Math.min(slotsPerWall, Math.ceil(sorted.length / 4));
    const offs = offsets(count);
    for (const off of offs) {
      if (i >= sorted.length) break;
      const art = sorted[i++];
      let position;
      if (wall.axis === "x") {
        position = [off, y, wall.wallZ];
      } else {
        position = [wall.wallX, y, off];
      }
      result.push({ artwork: art, wall: wall.name, position });
    }
    if (i >= sorted.length) break;
  }
  return result;
}

// ---------- Main scene component ----------
export default function MuseumRoom3D({ artworks, onOpen, isMobile = false }) {
  const [locked, setLocked] = useState(false);
  const controlsRef = useRef();
  const canvasEl = useRef(null);

  const placed = useMemo(() => distributePaintings(artworks), [artworks]);

  const handleEnter = () => {
    if (controlsRef.current) controlsRef.current.lock();
  };

  return (
    <div
      className="relative w-full h-full"
      data-testid="museum-room-3d"
      ref={canvasEl}
    >
      <Canvas
        camera={{ position: [0, 1.7, 0], fov: 75, near: 0.1, far: 100 }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        dpr={[1, 1.5]}
        style={{
          background: "#0b2830",
          cursor: locked ? "none" : "grab",
          touchAction: "none",
        }}
      >
        <Suspense fallback={null}>
          <Room />
        </Suspense>
        {placed.map((p) => (
          <Suspense key={p.artwork.id} fallback={null}>
            <Painting
              artwork={p.artwork}
              wall={p.wall}
              position={p.position}
              onOpen={onOpen}
            />
          </Suspense>
        ))}
        {/* Desktop only: PointerLock walk-mode. Mobile browsers don't support
            it reliably, so we skip it and rely on drag-to-look-around. */}
        {!isMobile && (
          <PointerLockControls
            ref={controlsRef}
            pointerSpeed={2.2}
            onLock={() => setLocked(true)}
            onUnlock={() => setLocked(false)}
          />
        )}
        <PanoramaCamera locked={locked} canvasEl={canvasEl} />
      </Canvas>

      {/* Desktop-only subtle CTA to unlock walk-mode. Doesn't block clicks. */}
      {!isMobile && !locked && (
        <button
          type="button"
          onClick={handleEnter}
          className="absolute bottom-6 right-6 sm:bottom-8 sm:right-8 z-10 px-4 py-2 text-xs tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/50 bg-black/40 backdrop-blur-sm hover:bg-black/60 hover:border-[#D4AF37] transition-colors"
          data-testid="enter-room-button"
        >
          Enter walk-mode →
        </button>
      )}

      {/* In-room HUD reticle */}
      {locked && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-[#D4AF37]/70 shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
        </div>
      )}

      {/* Drag hint (fades away after a moment) */}
      {!locked && (
        <div
          className="pointer-events-none absolute bottom-6 left-6 sm:bottom-8 sm:left-8 z-10 text-[10px] tracking-[0.3em] uppercase text-zinc-400 opacity-70"
          data-testid="drag-hint"
        >
          {isMobile ? "Drag to look around" : "Drag · Click a painting"}
        </div>
      )}
    </div>
  );
}
